import React from 'react';
import './GlowBorder.css';

const DEFAULT_GRADIENT = 'conic-gradient(#7c3aed, #a855f7, #06b6d4, #22d3ee, #a855f7, #7c3aed 80%)';
const DEFAULT_FILL = '#7c3aed';
const DEFAULT_FILL_HOVER = '#8b5cf6';

/**
 * Reusable animated border wrapper (rotating conic-gradient ring).
 * Forwards ref to the root div so it works as the single child of MUI Tooltip.
 *
 * Props:
 *  - size        — width/height in px when width/height omitted (default 46)
 *  - width       — optional explicit width in px
 *  - height      — optional explicit height in px
 *  - borderRadius — px number or CSS radius string (e.g. "0 8px 8px 0")
 *  - innerInset  — px gap for animated ring, number or { top, right, bottom, left }
 *  - gradient    — conic-gradient string for the rotating border
 *  - fill        — solid background color for the center cutout
 *  - fillHover   — center color on hover
 *  - style       — extra styles on the outer wrapper
 *  - className   — extra class on the outer wrapper
 */
const GlowBorder = React.forwardRef(({
  children,
  size = 46,
  width,
  height,
  borderRadius = 6,
  gradient = DEFAULT_GRADIENT,
  fill = DEFAULT_FILL,
  fillHover = DEFAULT_FILL_HOVER,
  innerInset = 2,
  style,
  className = '',
  ...rest
}, ref) => {
  const dimensionStyle = {
    width: width ?? size,
    height: height ?? size,
  };
  const radiusValue =
    typeof borderRadius === 'number' ? `${borderRadius}px` : borderRadius;
  const radiusStyle = { borderRadius: radiusValue };
  const inset =
    typeof innerInset === 'number'
      ? { top: innerInset, right: innerInset, bottom: innerInset, left: innerInset }
      : { top: 2, right: 2, bottom: 2, left: 2, ...innerInset };

  return (
    <div
      ref={ref}
      className={`glow-border ${className}`}
      style={{ ...dimensionStyle, ...style }}
      {...rest}
    >
      <div
        className="glow-border__border"
        style={{
          ...radiusStyle,
          '--glow-gradient': gradient,
          '--glow-fill': fill,
          '--glow-fill-hover': fillHover,
          '--glow-inset-top': `${inset.top}px`,
          '--glow-inset-right': `${inset.right}px`,
          '--glow-inset-bottom': `${inset.bottom}px`,
          '--glow-inset-left': `${inset.left}px`,
        }}
      />
      <div style={{ position: 'relative', zIndex: 3, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
});

GlowBorder.displayName = 'GlowBorder';

export default GlowBorder;
